'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { XIcon } from '@phosphor-icons/react';
import IconButton from '_components/UI/IconButton';
import TextButton from '_components/UI/TextButton';
import { useContext } from 'react';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { useRouter as useAppRouter } from 'next/navigation';
import { useLocale } from '@hooks/useLocale';
import { useUI } from '@context/GlobalUIContext';
import {
  AccountMenuLevels,
  trackAccountMenuEvents,
} from '_utils/GA4/trackAccountMenuEvents';

import styles from './LoggedOutContent.module.scss';

const LoggedOutContent = ({ onClose }: { onClose?: () => void }) => {
  const t = useTranslations('common');
  const locale = useLocale();
  const { openLoginModal } = useUI();

  // Dual-router compatibility: support both Pages Router and App Router
  const pagesRouter = useContext(RouterContext);
  const appRouter = useAppRouter();

  const handleSignInClick = () => {
    trackAccountMenuEvents({
      data: {
        level: AccountMenuLevels.level_2,
        linkUrl: null,
        linkText: t('nav.utility.account.new.signin'),
      },
    });
    onClose?.();
    openLoginModal();
  };

  const handleCreateAccountClick = () => {
    trackAccountMenuEvents({
      data: {
        level: AccountMenuLevels.level_2,
        linkUrl: `/${locale}/auth/register`,
        linkText: t('nav.utility.account.new.create.account'),
      },
    });
    onClose?.();
    const path = `/${locale}/auth/register`;
    if (pagesRouter) {
      pagesRouter.push(path);
    } else {
      appRouter.push(path);
    }
  };

  return (
    <>
      <div className={styles.header}>
        <Image
          src="/images/account_menu.png"
          alt="Reset Password"
          fill
          sizes="100vw"
          style={{
            objectFit: 'cover',
          }}
        />
        {onClose && (
          <div
            className={styles.closeButton}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
          >
            <IconButton
              icon={XIcon}
              type="secondary"
              shape="circular"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label={t('close')}
              iconSize={16}
              iconWeight="regular"
            />
          </div>
        )}
      </div>
      <div className={styles.content}>
        <h2 className={styles.heading}>
          {t('nav.utility.account.new.heading')}
        </h2>
        <p className={styles.description}>
          {t('nav.utility.account.new.description')}
        </p>
        <TextButton
          label={t('nav.utility.account.new.create.account')}
          size="large"
          type="primary"
          className={styles.createAccountButton}
          onClick={handleCreateAccountClick}
        />
        <TextButton
          label={t('nav.utility.account.new.signin')}
          size="large"
          type="secondary"
          className={styles.signInButton}
          onClick={handleSignInClick}
        />
      </div>
    </>
  );
};

export default LoggedOutContent;
